import { Modal } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import dayjs from "dayjs"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { showErrorNotifications } from "../../../../utils/handlers/handle-error.tsx"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import {
  CalendarAddEventForm,
  type CalendarEventFormSubmission
} from "../../../Calendar/CalendarPage/components/CalendarAddForms/CalendarAddEventForm.tsx"
import { partialUpdateEvent } from "../../../Calendar/api/services.ts"
import { getLeadDetails } from "../../api/services.ts"
import { leadDisplayName } from "../../helpers/lead-display-name.ts"
import { fetchLead } from "../../redux/lead.thunks.ts"
import { type Lead } from "../../types/lead.types.ts"
import { type LeadHeaderPatch } from "../components/LeadWorkspaceHeader/lead-header-patch.ts"
import {
  LeadScheduleFields,
  type LeadScheduleKind
} from "./LeadScheduleFields.tsx"
import {
  APPOINTMENT_DEFAULT_MINUTES,
  FOLLOW_UP_DEFAULT_MINUTES
} from "./schedule-durations.ts"

const TIME_FORMAT = "HH:mm"

/** The API localises time fields, so a 12-hour account sends "12:04 AM". */
const TIME_INPUT_FORMATS = [TIME_FORMAT, "H:mm", "h:mm A", "hh:mm A"]

const parseTime = (value?: string | null) => {
  if (!value) return null
  const parsed = dayjs(String(value), TIME_INPUT_FORMATS, true)
  return parsed.isValid() ? parsed : null
}

interface LeadScheduleEditorModalProps {
  kind: LeadScheduleKind | null
  lead: Lead
  owners: BaseOptionType[]
  saving?: boolean
  onChange: (patch: LeadHeaderPatch) => Promise<boolean>
  onClose: () => void
}

/**
 * One editor behind every follow-up / appointment trigger in the workspace —
 * the header reading, the Lifecycle card and the Quick Actions card all open it.
 */
function LeadScheduleEditorModal({
  kind,
  lead,
  owners,
  saving,
  onChange,
  onClose
}: LeadScheduleEditorModalProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [savingEvent, setSavingEvent] = useState(false)
  const { parseDate } = useDateFormat()
  const timezone = useAppSelector(
    (state) => state.auth?.userInfo?.timezone || "UTC"
  )

  // `lead_name` is optional, so the raw field leaves this Select showing
  // nothing to resolve a label from; the shared display-name fallback (also
  // used by the workspace header) keeps it readable instead of "Unavailable".
  const lockedLeadOption: BaseOptionType = useMemo(
    () => ({
      value: lead.id,
      label: leadDisplayName(lead, t("leadWorkspace.header.untitled"))
    }),
    [lead, t]
  )

  const appointmentEvent = useMemo(
    () =>
      lead?.lead_calendar_events?.find(
        (event) => event.id === lead?.appointment_event
      ),
    [lead?.appointment_event, lead?.lead_calendar_events]
  )

  const followUpEvent = useMemo(
    () =>
      lead?.lead_calendar_events?.find(
        (event) => event.id === lead?.follow_up_event
      ),
    [lead?.follow_up_event, lead?.lead_calendar_events]
  )

  const appointmentStart = useMemo(() => {
    if (appointmentEvent?.start_at) return dayjs(appointmentEvent.start_at).tz()

    const date = parseDate(lead?.appointment_date)
    const time = parseTime(lead?.appointment_time)
    if (!date || !time) return undefined

    return dayjs.tz(
      `${date.format(DATE_FORMAT)} ${time.format(TIME_FORMAT)}`,
      `${DATE_FORMAT} ${TIME_FORMAT}`,
      timezone
    )
  }, [
    appointmentEvent?.start_at,
    lead?.appointment_date,
    lead?.appointment_time,
    parseDate,
    timezone
  ])

  const appointmentInitialValues = useMemo(() => {
    const participants = appointmentEvent?.participants?.length
      ? appointmentEvent.participants.map((participant) => ({
          value: participant.id,
          label: participant.full_name
        }))
      : lead?.sales_manager?.map((participant) => ({
          value: participant.id,
          label: getEntityName(participant)
        }))
    const externalParticipants = appointmentEvent?.external_participants
      ?.filter((participant) => !participant.is_customer_invite)
      .map((participant) => participant.email)
    const customerInviteEmail = appointmentEvent?.external_participants?.find(
      (participant) => participant.is_customer_invite
    )?.email

    return {
      ...appointmentEvent,
      title:
        appointmentEvent?.title ||
        (lead.lead_name?.trim()
          ? `Appointment: ${lead.lead_name.trim()}`
          : "Appointment"),
      category: appointmentEvent?.category ?? "LEAD_MEETING",
      all_day: appointmentEvent?.all_day || false,
      date: appointmentStart,
      start_at: appointmentStart,
      end_at:
        appointmentEvent?.end_at ||
        appointmentStart?.add(APPOINTMENT_DEFAULT_MINUTES, "minute"),
      participants,
      external_participants: externalParticipants,
      customer_invite_email: customerInviteEmail,
      invite_customer: !!customerInviteEmail,
      lead: lockedLeadOption
    }
  }, [appointmentEvent, appointmentStart, lead.id, lead.lead_name])

  const followUpStart = useMemo(() => {
    if (followUpEvent?.start_at) return dayjs(followUpEvent.start_at).tz()

    const date = parseDate(lead?.follow_up_date)
    const time = parseTime(lead?.follow_up_time)
    if (!date || !time) return undefined

    return dayjs.tz(
      `${date.format(DATE_FORMAT)} ${time.format(TIME_FORMAT)}`,
      `${DATE_FORMAT} ${TIME_FORMAT}`,
      timezone
    )
  }, [followUpEvent?.start_at, lead, parseDate, timezone])

  const followUpInitialValues = useMemo(() => {
    const ownerOption = owners.find(
      (owner) => owner.value === lead.follow_up_owner
    )
    const participants = followUpEvent?.participants?.length
      ? followUpEvent.participants.map((participant) => ({
          value: participant.id,
          label: participant.full_name
        }))
      : ownerOption
        ? [ownerOption]
        : undefined
    const externalParticipants = followUpEvent?.external_participants
      ?.filter((participant) => !participant.is_customer_invite)
      .map((participant) => participant.email)
    const customerInviteEmail = followUpEvent?.external_participants?.find(
      (participant) => participant.is_customer_invite
    )?.email

    return {
      ...followUpEvent,
      title:
        followUpEvent?.title ||
        (lead.lead_name?.trim()
          ? `Follow-Up: ${lead.lead_name.trim()}`
          : "Follow-Up"),
      category: "FOLLOW_UP",
      all_day: followUpEvent?.all_day || false,
      follow_up_owner: lead.follow_up_owner ?? undefined,
      date: followUpStart,
      start_at: followUpStart,
      end_at:
        followUpEvent?.end_at ||
        followUpStart?.add(FOLLOW_UP_DEFAULT_MINUTES, "minute"),
      participants,
      external_participants: externalParticipants,
      customer_invite_email: customerInviteEmail,
      invite_customer: !!customerInviteEmail,
      lead: lockedLeadOption
    }
  }, [followUpEvent, followUpStart, lead, owners])

  const close = () => {
    onClose()
  }

  const submitScheduledEvent = async (
    scheduleKind: Exclude<LeadScheduleKind, null>,
    values: CalendarEventFormSubmission
  ) => {
    const start = values.all_day
      ? dayjs.tz(values.date, DATE_FORMAT, timezone).startOf("day")
      : dayjs(values.start_at).tz(timezone)
    const end = dayjs(values.end_at).tz(timezone)
    const duration = Math.max(
      15,
      Math.ceil(end.diff(start, "minute") / 15) * 15
    )
    const followUpOwner = values.follow_up_owner as string | undefined
    const leadPatch: LeadHeaderPatch =
      scheduleKind === "appointment"
        ? {
            appointment_date: start.format(DATE_FORMAT),
            appointment_time: start.format(TIME_FORMAT),
            appointment_duration_minutes: duration
          }
        : {
            follow_up_owner: followUpOwner ?? null,
            follow_up_date: start.format(DATE_FORMAT),
            follow_up_time: start.format(TIME_FORMAT),
            follow_up_duration_minutes: duration
          }

    setSavingEvent(true)
    try {
      const saved = await onChange(leadPatch)
      if (!saved) return

      const leadResponse = await getLeadDetails(lead.id)
      if (!leadResponse.ok) {
        throw leadResponse.data?.details || new Error("Unable to reload lead")
      }

      const eventId =
        scheduleKind === "appointment"
          ? leadResponse.data?.appointment_event
          : leadResponse.data?.follow_up_event
      if (!eventId) {
        throw new Error(
          `The ${scheduleKind === "appointment" ? "appointment" : "follow-up"} event was not created`
        )
      }

      const { follow_up_owner: _followUpOwner, ...eventValues } = values
      const calendarValues =
        scheduleKind === "followUp" && followUpOwner && eventValues.participants
          ? {
              ...eventValues,
              participants: [
                followUpOwner,
                ...eventValues.participants.filter(
                  (participant) =>
                    participant !== followUpOwner &&
                    participant !== lead.follow_up_owner
                )
              ]
            }
          : eventValues

      const eventResponse = await partialUpdateEvent({
        id: eventId,
        ...calendarValues,
        lead: lead.id,
        // Omitting this on PATCH preserves a stale Calendar project relation.
        project: null,
        category: scheduleKind === "appointment" ? "LEAD_MEETING" : "FOLLOW_UP"
      })
      if (!eventResponse.ok) {
        throw (
          eventResponse.data?.details ||
          new Error(`Unable to save ${scheduleKind} details`)
        )
      }

      await dispatch(fetchLead(lead.id))
      close()
    } catch (error) {
      showErrorNotifications(error)
    } finally {
      setSavingEvent(false)
    }
  }

  if (kind === "appointment") {
    return (
      <Modal
        open
        destroyOnHidden
        maskClosable={false}
        footer={null}
        width={600}
        title={
          lead?.appointment_event ? "Edit Appointment" : "Create Appointment"
        }
        onCancel={close}
      >
        <div
          className={"overflow-y-auto flex flex-col size-full"}
          style={{ maxHeight: "calc(100vh - 100px)" }}
        >
          <CalendarAddEventForm
            initialValues={appointmentInitialValues}
            loading={saving || savingEvent}
            timezone={timezone}
            lockedLead={lockedLeadOption}
            submitLabel={
              lead?.appointment_event
                ? "Save Appointment"
                : "Create Appointment"
            }
            isEdit={!!lead?.appointment_event}
            onClose={close}
            onSubmit={(values) => submitScheduledEvent("appointment", values)}
          />
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={kind === "followUp"}
      destroyOnHidden
      maskClosable={false}
      footer={null}
      width={600}
      title={lead?.follow_up_event ? "Edit Follow-Up" : "Create Follow-Up"}
      onCancel={close}
    >
      <div
        className={"overflow-y-auto flex flex-col size-full"}
        style={{ maxHeight: "calc(100vh - 100px)" }}
      >
        <CalendarAddEventForm
          initialValues={followUpInitialValues}
          loading={saving || savingEvent}
          timezone={timezone}
          lockedLead={lockedLeadOption}
          submitLabel={
            lead?.follow_up_event ? "Save Follow-Up" : "Create Follow-Up"
          }
          additionalFields={
            <LeadScheduleFields
              kind={"followUp"}
              owners={owners}
              showDate={false}
              showTime={false}
              required
            />
          }
          isEdit={!!lead?.follow_up_event}
          onClose={close}
          onSubmit={(values) => submitScheduledEvent("followUp", values)}
        />
      </div>
    </Modal>
  )
}

export { type LeadScheduleKind, LeadScheduleEditorModal }
